// Định nghĩa key lưu trữ chuẩn
export const STORAGE = {
  TOKEN: 'token-qlrv_hd',
  USER_INFO: 'user-info-qlrv_hd',
  BROADCAST: 'broadcast-qlrv',
  BROADCAST_POPUP: 'broadcast-popup-qlrv',
  REMEMBER_LOGIN: 'remember-login',
  KEY_MENU_ACTIVE: 'key-active',
  TABS_PAGE_ACTIVE: 'tabs-page-active',
  ERROR_EXTENSION: 'error-extension',
  DEV_MODE: 'dev-mode',
  IS_DARK_MODE: 'is-dark-mode',
  IS_SOUND_NOTIF: 'is-sound-notif',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  REMEMBERED_LOGIN_FORM: 'remembered-login-form',
  THEME: 'theme',
  IS_LOGGED_IN: 'is-logged-in',
  REMEMBER_HOME: 'remember-home',
} as const

// Type của các key
export type StorageKey = (typeof STORAGE)[keyof typeof STORAGE]

// --------- LocalStorage -----------

export const getLocalStorage = <T = unknown>(key: StorageKey): T | null => {
  const data = localStorage.getItem(key)
  try {
    return data ? JSON.parse(data) : null
  } catch {
    return data as unknown as T
  }
}

export const setLocalStorage = (key: StorageKey, value: unknown): void => {
  const stringify = typeof value === 'string' ? value : JSON.stringify(value)
  localStorage.setItem(key, stringify)
}

export const deleteLocalStorage = (key: StorageKey): void => {
  localStorage.removeItem(key)
}

export const clearLocalStorage = (): void => {
  localStorage.clear()
}

// --------- SessionStorage -----------

export const getSessionStorage = <T = unknown>(key: StorageKey): T | null => {
  if (typeof window === 'undefined') return null
  const data = sessionStorage.getItem(key)
  try {
    return data ? JSON.parse(data) : null
  } catch {
    return data as unknown as T
  }
}

export const setSessionStorage = (key: StorageKey, value: unknown): void => {
  const stringify = typeof value === 'string' ? value : JSON.stringify(value)
  sessionStorage.setItem(key, stringify)
}

export const deleteSessionStorage = (key: StorageKey): void => {
  sessionStorage.removeItem(key)
}

export const clearSessionStorage = (): void => {
  sessionStorage.clear()
}

import { decryptToken, encryptToken } from '../crypto'

// ponytail: setAuthToken/setRefreshToken lưu bản đã encryptToken() — đọc lại phải
// decryptToken() tương ứng, không thì gửi thẳng ciphertext làm Bearer token lên BE
export const getAuthToken = (): string | null => {
  const raw = getSessionStorage<string>(STORAGE.TOKEN) ?? getLocalStorage<string>(STORAGE.TOKEN)
  return raw ? decryptToken(raw) : null
}
// ponytail: HQC-228 root cause. Token có thể nằm ở sessionStorage (remember=false)
// hoặc localStorage (remember=true / OAuth). Login flag PHẢI đọc CẢ HAI — đọc một
// mình localStorage là nguyên nhân redirect /login loop khi KHÔNG tích Ghi nhớ.
// Không decrypt ở đây vì OAuth path lưu raw token (decryptToken sẽ trả null).
export const isLoggedIn = (): boolean =>
  getSessionStorage<string>(STORAGE.TOKEN) !== null ||
  getLocalStorage<string>(STORAGE.TOKEN) !== null
export const getRefreshToken = (): string | null => {
  const raw =
    getSessionStorage<string>(STORAGE.REFRESH_TOKEN) ??
    getLocalStorage<string>(STORAGE.REFRESH_TOKEN)
  return raw ? decryptToken(raw) : null
}

export const setAuthToken = (token: string, remember: boolean): void => {
  const encrypted = encryptToken(token)
  if (remember) {
    setLocalStorage(STORAGE.TOKEN, encrypted)
    deleteSessionStorage(STORAGE.TOKEN)
  } else {
    setSessionStorage(STORAGE.TOKEN, encrypted)
    deleteLocalStorage(STORAGE.TOKEN)
  }
}

export const setRefreshToken = (token: string, remember: boolean): void => {
  const encrypted = encryptToken(token)
  if (remember) {
    setLocalStorage(STORAGE.REFRESH_TOKEN, encrypted)
    deleteSessionStorage(STORAGE.REFRESH_TOKEN)
  } else {
    setSessionStorage(STORAGE.REFRESH_TOKEN, encrypted)
    deleteLocalStorage(STORAGE.REFRESH_TOKEN)
  }
}

export const deleteAuthToken = (): void => {
  deleteLocalStorage(STORAGE.TOKEN)
  deleteSessionStorage(STORAGE.TOKEN)
}

export const storage = {
  getLocalStorage,
  setLocalStorage,
  deleteLocalStorage,
  clearLocalStorage,
  getSessionStorage,
  setSessionStorage,
  deleteSessionStorage,
  clearSessionStorage,
  getAuthToken,
  setAuthToken,
  deleteAuthToken,
  isLoggedIn,
}
